import React from 'react';
import styled, { css } from 'styled-components';

type ButtonVariant = 'primary' | 'secondary' | 'outlined';
type ButtonSize = 'small' | 'medium' | 'large';

interface ButtonProps {
  children: React.ReactNode;
  variant?: ButtonVariant;
  size?: ButtonSize;
  fullWidth?: boolean;
  onClick?: () => void;
  type?: 'button' | 'submit' | 'reset';
  disabled?: boolean;
}

const StyledButtonWrapper = styled.button<{
  $variant: ButtonVariant;
  $size: ButtonSize;
  $fullWidth: boolean;
}>`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  font-weight: 500;
  border: none;
  outline: none;
  text-transform: uppercase;
  letter-spacing: 1px;
  transition: var(--transition-medium);
  cursor: pointer;
  white-space: nowrap;
  
  ${props => props.$fullWidth && css`
    width: 100%;
  `}
  
  /* Mobile responsive adjustments */
  @media (max-width: 768px) {
    font-size: 0.9rem !important;
    letter-spacing: 0.5px;
    
    ${props => props.$fullWidth && css`
      width: 100%;
      max-width: 300px;
      margin: 0 auto;
    `}
  }
  
  @media (max-width: 480px) {
    font-size: 0.85rem !important;
    padding: 12px 20px !important;
    
    ${props => props.$size === 'large' && css`
      padding: 14px 24px !important;
    `}
  }
  cursor: pointer;
  
  ${props => props.$fullWidth && css`
    width: 100%;
  `}
  
  /* Variants */
  ${props => {
    switch (props.$variant) {
      case 'primary':
        return css`
          background-color: var(--accent);
          color: var(--background-color);
          
          &:hover {
            background-color: var(--primary-color);
            color: var(--text-light);
          }
        `;
      case 'secondary':
        return css`
          background-color: var(--primary-color);
          color: var(--text-light);
          
          &:hover {
            background-color: var(--accent);
            color: var(--primary-color);
          }
        `;
      case 'outlined':
        return css`
          background-color: transparent;
          color: var(--accent);
          border: 2px solid var(--accent);
          
          &:hover {
            background-color: var(--accent);
            color: var(--background-color);
          }
        `;
      default:
        return '';
    }
  }}
  
  /* Sizes */
  ${props => {
    switch (props.$size) {
      case 'small':
        return css`
          padding: 8px 16px;
          font-size: 0.875rem;
          border-radius: var(--border-radius-sm);
        `;
      case 'medium':
        return css`
          padding: 12px 24px;
          font-size: 1rem;
          border-radius: var(--border-radius-md);
        `;
      case 'large':
        return css`
          padding: 16px 32px;
          font-size: 1.125rem;
          border-radius: var(--border-radius-md);
        `;
      default:
        return '';
    }
  }}
  
  &:disabled {
    opacity: 0.6;
    cursor: not-allowed;
  }
`;

const StyledButton: React.FC<ButtonProps> = ({
  children,
  variant = 'primary',
  size = 'medium',
  fullWidth = false,
  onClick,
  type = 'button',
  disabled = false,
}) => {
  return (
    <StyledButtonWrapper
      $variant={variant}
      $size={size}
      $fullWidth={fullWidth}
      onClick={onClick}
      type={type}
      disabled={disabled}
    >
      {children}
    </StyledButtonWrapper>
  );
};

export default StyledButton; 